/**
 * FilterContext — owns the active Themes + Property Types filter state
 * for the current project, plus the chip catalogue itself.
 *
 * Mounting note: we sit INSIDE ProjectProvider in App.js so we can react
 * to project changes (city navigation) by refetching the catalogue and
 * resetting selections. The context exposes a stable API regardless of
 * whether the catalogue has loaded yet so consumers (FilterBar, map
 * coloring) don't need their own loading guards.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useProject } from "./ProjectContext";
import {
  fetchTaxonomies,
  indexTaxonomiesById,
  shapeMatchesActiveLens,
  pickPrimaryChipColor,
  TAXONOMY_KIND,
  KIND_ORDER,
} from "./utils/taxonomy";

const FilterContext = createContext(null);

// Persistence key for the "Display Regions" toggle. Per the
// 2026-05-13 design review, this state survives page reloads (it's a
// view preference, not a filter the URL needs to encode).
//
// Bumped to v2 in 2026-05-13 follow-up: default flipped from ON to OFF
// because polygon overlays clutter the map on first load and most users
// want to see markers first. Bumping the key forces the new default to
// take effect even for users whose browser cached the old ON pref.
const REGIONS_STORAGE_KEY = "whoseland.filter.regionsVisible.v2";

// Persistence key for the active-kind toggle (the FilterBar's vertical
// segmented control). Per the June 10 2026 client wireframes, the strip
// paints one kind's chip row at a time and the segmented toggle swaps the
// active lens. The user's choice survives reloads + project switches
// because it's a view preference, not a content filter — switching the
// lens does not clear any kind's selections.
//
// Bumped to v2 for catalogue v3 (Communities added as a third kind). The
// v1 default was "theme"; v2 defaults to "community" so Communities is the
// lens painting on first load (client decision). Bumping the key resets
// returning users to the new default once — their stale v1 "theme" pref is
// ignored — matching how regionsVisible / the expanded pref were
// re-defaulted on prior redesigns.
const KIND_STORAGE_KEY = "whoseland.filterBar.activeKind.v2";
const DEFAULT_KIND = TAXONOMY_KIND.COMMUNITY;
const VALID_KINDS = new Set(KIND_ORDER);

// Shared read-only empty set for the active-lens selector fallback.
// Never mutated — selection state always flows through setState with a
// fresh Set, so handing consumers this singleton is safe.
const EMPTY_ID_SET = new Set();

// "Type of Entry" (Display settings). The June 2026 client wireframe
// makes this a SINGLE-SELECT radio group:
//   All entries / Verified history / Unverified history / Personal memory
// We keep the underlying Set representation (so the backend `entry_types`
// CSV contract is unchanged) but the UI only ever sets one of these four
// canonical states:
//   All entries        -> { personal, verified, unverified }  (no filter)
//   Verified history   -> { verified }
//   Unverified history -> { unverified }
//   Personal memory    -> { personal }
// The default radio is "All entries" (show everything). The set survives
// reloads as a view preference; we DO reset on logout (reload() below).
//
// Bumped to v2 (was v1): the old default was the Personal+Verified
// multi-select. Bumping the key resets returning users to the new
// All-entries default once rather than honoring a stale v1 pref that no
// longer maps to a single radio option.
const ENTRY_TYPES_STORAGE_KEY = "whoseland.filter.entryTypes.v2";
const VALID_ENTRY_TYPES = Object.freeze(["personal", "verified", "unverified"]);
const VALID_ENTRY_TYPES_SET = new Set(VALID_ENTRY_TYPES);
const DEFAULT_ENTRY_TYPES = Object.freeze([
  "personal",
  "verified",
  "unverified",
]);

// "Show My Places" toggle (DisplayControlsBar). Lives in context (not
// MapComponent local state) now that the toolbar control sits in its
// own component — both components need to read the same boolean.
// Persisted so the user's "I only care about my own pins" preference
// sticks across reloads.
// Bumped to v2 for PR #5 (three-state mode):
//   - all_places     (default)
//   - my_places_only
//   - hide_my_places
const SHOW_MY_PLACES_STORAGE_KEY = "whoseland.filter.showMyPlaces.v2";
export const SHOW_MY_PLACES_MODE = Object.freeze({
  ALL: "all_places",
  ONLY_MINE: "my_places_only",
  HIDE_MINE: "hide_my_places",
});
const VALID_SHOW_MY_PLACES_MODES = new Set(Object.values(SHOW_MY_PLACES_MODE));

const readRegionsPref = () => {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(REGIONS_STORAGE_KEY);
    if (raw === null) return false;
    return raw === "true";
  } catch (_e) {
    return false;
  }
};

const writeRegionsPref = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(REGIONS_STORAGE_KEY, value ? "true" : "false");
  } catch (_e) {
    // Safari private mode etc. — preference reverts to default next load.
  }
};

const readKindPref = () => {
  if (typeof window === "undefined") return DEFAULT_KIND;
  try {
    const raw = window.localStorage.getItem(KIND_STORAGE_KEY);
    if (raw && VALID_KINDS.has(raw)) return raw;
  } catch (_e) {
    /* fall through */
  }
  return DEFAULT_KIND;
};

const writeKindPref = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KIND_STORAGE_KEY, value);
  } catch (_e) {
    // Safari private mode etc. — preference reverts to default next load.
  }
};

const readEntryTypesPref = () => {
  if (typeof window === "undefined") return new Set(DEFAULT_ENTRY_TYPES);
  try {
    const raw = window.localStorage.getItem(ENTRY_TYPES_STORAGE_KEY);
    if (raw === null) return new Set(DEFAULT_ENTRY_TYPES);
    // Stored as a CSV. An empty string is a legitimate "0 buckets
    // selected" state (the user toggled them all off) — preserve it
    // rather than falling back to the default, otherwise the user
    // loses their explicit choice on reload.
    const parts = raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter((s) => VALID_ENTRY_TYPES_SET.has(s));
    return new Set(parts);
  } catch (_e) {
    return new Set(DEFAULT_ENTRY_TYPES);
  }
};

const writeEntryTypesPref = (set) => {
  if (typeof window === "undefined") return;
  try {
    // Sort so the persisted form is canonical (helps debugging and
    // makes localStorage diffs stable across browser sessions).
    const csv = Array.from(set).sort().join(",");
    window.localStorage.setItem(ENTRY_TYPES_STORAGE_KEY, csv);
  } catch (_e) {
    /* fall through */
  }
};

const readShowMyPlacesPref = () => {
  if (typeof window === "undefined") return SHOW_MY_PLACES_MODE.ALL;
  try {
    const raw = window.localStorage.getItem(SHOW_MY_PLACES_STORAGE_KEY);
    return VALID_SHOW_MY_PLACES_MODES.has(raw)
      ? raw
      : SHOW_MY_PLACES_MODE.ALL;
  } catch (_e) {
    return SHOW_MY_PLACES_MODE.ALL;
  }
};

const writeShowMyPlacesPref = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SHOW_MY_PLACES_STORAGE_KEY, value);
  } catch (_e) {
    /* fall through */
  }
};

// "Highlight my places" — the top-right personal-account toggle. This is a
// PURE VISUAL HIGHLIGHT, decoupled from the showMyPlaces FILTER above
// (client ask, July 2026: "Show my places" must ADD big black pins to the
// user's own sites, never remove/hide any places). Default OFF so the pins
// are only revealed after the user clicks "Show my places" — matching the
// original design. Persisted like the other view prefs.
const HIGHLIGHT_MY_PLACES_STORAGE_KEY = "whoseland.filter.highlightMyPlaces.v1";
const readHighlightMyPlacesPref = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(HIGHLIGHT_MY_PLACES_STORAGE_KEY) === "true";
  } catch (_e) {
    return false;
  }
};
const writeHighlightMyPlacesPref = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      HIGHLIGHT_MY_PLACES_STORAGE_KEY,
      value ? "true" : "false",
    );
  } catch (_e) {
    /* fall through */
  }
};

export const useFilter = () => {
  const ctx = useContext(FilterContext);
  // Defensive default: pages outside the provider (admin) can call this
  // and get a no-op surface instead of crashing.
  if (!ctx) {
    return {
      taxonomies: [],
      taxonomyIndex: new Map(),
      activeThemeIds: new Set(),
      activePropertyTypeIds: new Set(),
      activeCommunityIds: new Set(),
      activeKind: DEFAULT_KIND,
      // Effective lens after the seeded-kind fallback. With no provider
      // there's no catalogue, so it mirrors the stored/default kind.
      effectiveKind: DEFAULT_KIND,
      dateAddedDays: null,
      entryTypes: new Set(DEFAULT_ENTRY_TYPES),
      selectedContributorIds: [],
      regionsVisible: false,
      showMyPlaces: SHOW_MY_PLACES_MODE.ALL,
      highlightMyPlaces: false,
      isAnyActive: false,
      isAnyTaxActive: false,
      isActiveLensFiltered: false,
      loading: false,
      toggleTheme: () => {},
      togglePropertyType: () => {},
      toggleCommunity: () => {},
      setActiveKind: () => {},
      setDateAddedDays: () => {},
      toggleEntryType: () => {},
      setEntryTypes: () => {},
      setSelectedContributorIds: () => {},
      setRegionsVisible: () => {},
      setShowMyPlaces: () => {},
      setHighlightMyPlaces: () => {},
      clearAll: () => {},
      refresh: () => {},
      shapePassesFilter: () => true,
      activeSelectedIds: new Set(),
      // colorForShape signature: (shapeTaxonomyIds, defaultColor, themeRanks?).
      // The third arg is the per-shape rank map emitted by selectProjectCont
      // (PR #43+44) — optional so call sites without ranks still work.
      colorForShape: (_ids, defaultColor) => defaultColor,
    };
  }
  return ctx;
};

// Date Added bucket presets surfaced in the UI. We dropped the 90-day
// bucket in the 2026-05-13 design review — usage data was sparse and
// the wireframes show a tighter "any / week / month" trio. Keep the
// preset list in module scope so FilterBar renders one canonical copy.
export const DATE_ADDED_PRESETS = Object.freeze([
  { value: 7, label: "This week" },
  { value: 30, label: "This month" },
]);

// Type of Entry pills (DisplayControlsBar). Multi-select, independent
// toggles per the 2026-06 wireframes. `value` is the bucket id used
// by both the local Set and the backend `entry_types` CSV.  Ordered
// to match the wireframe (Personal · Verified · Unverified, left→right).
//
// Defaults are derived from DEFAULT_ENTRY_TYPES at module scope (see
// the storage helpers above). Don't hard-code defaults at call sites;
// import `getDefaultEntryTypes()` if a consumer needs them.
export const ENTRY_TYPE_OPTIONS = Object.freeze([
  { value: "personal",   label: "Personal Memory" },
  { value: "verified",   label: "History (verified)" },
  { value: "unverified", label: "History (unverified)" },
]);

export const getDefaultEntryTypes = () => new Set(DEFAULT_ENTRY_TYPES);

export const FilterProvider = ({ children }) => {
  const { project } = useProject();
  const projectId = project?.projectsid;

  const [taxonomies, setTaxonomies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeThemeIds, setActiveThemeIds] = useState(new Set());
  const [activePropertyTypeIds, setActivePropertyTypeIds] = useState(new Set());
  // Communities (catalogue v3, June 10 2026). Parallel single-select-style
  // chip set; lives alongside the theme/property sets so switching the
  // active lens never drops a selection. Wired into color + filter by the
  // active-lens model (see colorForShape / shapePassesFilter).
  const [activeCommunityIds, setActiveCommunityIds] = useState(new Set());
  // Date Added filter — null = any time, or a positive integer number
  // of days back (must be one of DATE_ADDED_PRESETS values, but we
  // don't enforce here so the setter stays cheap).
  const [dateAddedDays, setDateAddedDaysState] = useState(null);
  // Type of Entry — Set of bucket ids ('personal' | 'verified' |
  // 'unverified'). The Display-settings UI drives this as a single-select
  // radio group (All entries / Verified history / Unverified history /
  // Personal memory), but we keep the Set representation so the backend
  // `entry_types` CSV contract is unchanged. All-three-on = "no filter"
  // (the backend collapses it). Per design-review decision T6 we DO NOT
  // add a server column — we overload `is_reliable` + `visibility`.
  //
  // Initial value is read from localStorage so a returning user lands on
  // whatever radio they last picked.
  const [entryTypes, setEntryTypesState] = useState(readEntryTypesPref);
  // Contributor multi-select. Each entry is a numeric user id from
  // submitted_by; empty = "any contributor".
  const [selectedContributorIds, setSelectedContributorIdsState] = useState([]);
  // Display Regions toggle controls whether polygon/polyline overlays
  // render at all on the map. Persisted to localStorage (decision T4)
  // because it's a view preference, not a content filter.
  const [regionsVisible, setRegionsVisibleState] = useState(readRegionsPref);
  // Active taxonomy kind shown in the expanded panel — "theme" or
  // "property_type". Per the 2026-06 client wireframes, the expanded
  // filter strip only paints one kind's chip row at a time and a small
  // dropdown swaps which one. Inactive-kind selections persist in their
  // own Set so toggling kind is a pure view change (no data loss).
  // Persisted to localStorage like the other view preferences; survives
  // project switches deliberately (a viewer who set "Property Types"
  // expects that to stick when they navigate cities).
  const [activeKind, setActiveKindState] = useState(readKindPref);

  // "Show My Places" toggle (DisplayControlsBar). Lives here so the
  // top-right toolbar control and the MapComponent marker render
  // loop both read from the same source. Persisted so the user's
  // "I only care about my pins" preference sticks across reloads.
  const [showMyPlaces, setShowMyPlacesState] = useState(readShowMyPlacesPref);

  // Pure highlight toggle (top-right "Show my places"). Adds big black pins
  // to the user's own sites without filtering the map. Default OFF.
  const [highlightMyPlaces, setHighlightMyPlacesState] = useState(
    readHighlightMyPlacesPref,
  );

  // Refetch + reset selections whenever the project changes. We always
  // reset because chip ids are per-project — a Theme id valid in
  // /cityofmadison would point to nothing (or worse, a chip from a
  // different city) in /northmilwaukee.
  const reload = useCallback(async () => {
    if (!projectId) {
      setTaxonomies([]);
      setActiveThemeIds(new Set());
      setActivePropertyTypeIds(new Set());
      setActiveCommunityIds(new Set());
      setDateAddedDaysState(null);
      setSelectedContributorIdsState([]);
      // Intentionally do NOT reset entryTypes / regionsVisible /
      // showMyPlaces — they're view prefs that survive project
      // switches (the user picked "hide unverified everywhere", not
      // "hide it in this one city"). Same logic as the existing
      // regionsVisible carry-over.
      return;
    }
    setLoading(true);
    try {
      const list = await fetchTaxonomies(projectId);
      setTaxonomies(list);
    } finally {
      setLoading(false);
      setActiveThemeIds(new Set());
      setActivePropertyTypeIds(new Set());
      setActiveCommunityIds(new Set());
      setDateAddedDaysState(null);
      setSelectedContributorIdsState([]);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // O(1) lookup map; rebuilt only when the catalogue itself changes.
  const taxonomyIndex = useMemo(
    () => indexTaxonomiesById(taxonomies),
    [taxonomies],
  );

  // Which kinds this project actually has chips for. A partially-
  // configured admin project might seed only Themes; the active lens
  // must fall back to a seeded kind so the map never paints through an
  // empty dimension.
  const seededKinds = useMemo(() => {
    const s = new Set();
    for (const t of taxonomies) if (t && t.kind) s.add(t.kind);
    return s;
  }, [taxonomies]);

  // Resolved active lens: the user's stored kind if the project seeded
  // it, else the first seeded kind in canonical order. This is the
  // single source of truth for "which lens is painting / filtering" —
  // FilterBar consumes it so its segmented toggle highlight, the map
  // coloring, and the filter gate can never disagree.
  const effectiveKind = useMemo(() => {
    if (seededKinds.has(activeKind)) return activeKind;
    return KIND_ORDER.find((k) => seededKinds.has(k)) || activeKind;
  }, [activeKind, seededKinds]);

  // The active lens's own selection Set. Only this set filters + scopes
  // color; the inactive lenses' selections are retained but dormant.
  const activeSelectedIds = useMemo(() => {
    if (effectiveKind === TAXONOMY_KIND.THEME) return activeThemeIds;
    if (effectiveKind === TAXONOMY_KIND.PROPERTY_TYPE) return activePropertyTypeIds;
    if (effectiveKind === TAXONOMY_KIND.COMMUNITY) return activeCommunityIds;
    return EMPTY_ID_SET;
  }, [effectiveKind, activeThemeIds, activePropertyTypeIds, activeCommunityIds]);

  const toggleTheme = useCallback((id) => {
    const numId = Number(id);
    setActiveThemeIds((prev) => {
      const next = new Set(prev);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return next;
    });
  }, []);

  const togglePropertyType = useCallback((id) => {
    const numId = Number(id);
    setActivePropertyTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return next;
    });
  }, []);

  const toggleCommunity = useCallback((id) => {
    const numId = Number(id);
    setActiveCommunityIds((prev) => {
      const next = new Set(prev);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return next;
    });
  }, []);

  // Toggle-style setter so callers can pass `null` to clear or pass the
  // currently-active value to deactivate (single-select bucket UX).
  const setDateAddedDays = useCallback((value) => {
    if (value === null || value === undefined) {
      setDateAddedDaysState(null);
      return;
    }
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return;
    setDateAddedDaysState((prev) => (prev === num ? null : num));
  }, []);

  // Toggle a single entry-type pill. We persist on every mutation
  // so a refresh restores the user's exact pill state, including
  // the legitimate "all off" zero-rows state (see D-LOCK-1).
  const toggleEntryType = useCallback((value) => {
    const key = String(value).toLowerCase();
    if (!VALID_ENTRY_TYPES_SET.has(key)) return;
    setEntryTypesState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeEntryTypesPref(next);
      return next;
    });
  }, []);

  // Bulk setter for "Clear all" / programmatic resets. Accepts any
  // iterable of bucket ids; filters to the valid whitelist so a
  // bogus value can't poison the persisted state.
  const setEntryTypes = useCallback((iterable) => {
    let next;
    if (iterable && typeof iterable[Symbol.iterator] === "function") {
      next = new Set();
      for (const v of iterable) {
        const key = String(v).toLowerCase();
        if (VALID_ENTRY_TYPES_SET.has(key)) next.add(key);
      }
    } else {
      next = new Set(DEFAULT_ENTRY_TYPES);
    }
    setEntryTypesState(next);
    writeEntryTypesPref(next);
  }, []);

  const setSelectedContributorIds = useCallback((next) => {
    // Normalize to a sorted array of unique numeric ids so consumers
    // can compare with simple JSON equality / array length checks.
    if (!Array.isArray(next)) {
      setSelectedContributorIdsState([]);
      return;
    }
    const cleaned = Array.from(
      new Set(
        next
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    ).sort((a, b) => a - b);
    setSelectedContributorIdsState(cleaned);
  }, []);

  const setRegionsVisible = useCallback((value) => {
    const next = !!value;
    setRegionsVisibleState(next);
    writeRegionsPref(next);
  }, []);

  const setShowMyPlaces = useCallback((value) => {
    // Backward-compatible bool handling:
    //   true  -> my_places_only
    //   false -> all_places
    let next = value;
    if (typeof value === "boolean") {
      next = value
        ? SHOW_MY_PLACES_MODE.ONLY_MINE
        : SHOW_MY_PLACES_MODE.ALL;
    }
    if (!VALID_SHOW_MY_PLACES_MODES.has(next)) return;
    setShowMyPlacesState(next);
    writeShowMyPlacesPref(next);
  }, []);

  const setHighlightMyPlaces = useCallback((value) => {
    const next = !!value;
    setHighlightMyPlacesState(next);
    writeHighlightMyPlacesPref(next);
  }, []);

  // Setter validates against the whitelisted taxonomy kinds so bogus
  // values from localStorage tampering or future-kind code paths can
  // never wedge the UI into an empty state. Unknown inputs are a no-op
  // rather than a silent reset — defensive but predictable.
  const setActiveKind = useCallback((kind) => {
    if (!VALID_KINDS.has(kind)) return;
    setActiveKindState(kind);
    writeKindPref(kind);
  }, []);

  // "Clear All" wipes EVERY *content* filter dimension per decision T1
  // of the 2026-05-13 design review, but does NOT touch the
  // regionsVisible or showMyPlaces view preferences (those are layer
  // toggles, not content filters). entryTypes resets to the default
  // ("All entries" — all three buckets, June 2026 wireframe) so "Clear
  // all" returns the user to a known, fully-populated baseline.
  const clearAll = useCallback(() => {
    setActiveThemeIds(new Set());
    setActivePropertyTypeIds(new Set());
    setActiveCommunityIds(new Set());
    setDateAddedDaysState(null);
    const defaults = new Set(DEFAULT_ENTRY_TYPES);
    setEntryTypesState(defaults);
    writeEntryTypesPref(defaults);
    setSelectedContributorIdsState([]);
  }, []);

  // "Any chip selected in ANY lens" — drives Clear-all visibility so a
  // selection lingering in a now-inactive lens can still be cleared.
  const isAnyTaxActive =
    activeThemeIds.size > 0 ||
    activePropertyTypeIds.size > 0 ||
    activeCommunityIds.size > 0;
  // "Is the ACTIVE lens filtering?" — only the active kind's selection
  // hides/recolors shapes (active-lens model). This is the gate the map
  // uses to decide whether a chip selection is narrowing the view.
  const isActiveLensFiltered = activeSelectedIds.size > 0;
  // entryTypes contributes to "any filter active" when it diverges
  // from the locked default (D-LOCK-1: Personal + Verified). We compare
  // as sets so order doesn't matter and the "Clear all" pill state
  // (default) doesn't falsely light up the Clear button.
  const isEntryTypesDefault =
    entryTypes.size === DEFAULT_ENTRY_TYPES.length &&
    DEFAULT_ENTRY_TYPES.every((v) => entryTypes.has(v));
  const isAnyActive =
    isAnyTaxActive ||
    dateAddedDays != null ||
    !isEntryTypesDefault ||
    selectedContributorIds.length > 0;

  // Per-shape selectors closed over the current state so consumers
  // (MapComponent) can call them inside their own render loops without
  // re-deriving the index every iteration.
  //
  // Date gate: if `dateAddedDays` is set, a shape passes only when its
  // `created_at` is within the window. Shapes that lack the timestamp
  // fail closed — we'd rather hide the rare untyped row than mislead
  // a viewer who explicitly asked for "added in the last 30 days".
  const shapePassesFilter = useCallback(
    (shapeTaxonomyIds, createdAt) => {
      const taxOk = shapeMatchesActiveLens(
        shapeTaxonomyIds,
        activeSelectedIds,
        taxonomyIndex,
        effectiveKind,
      );
      if (!taxOk) return false;
      if (dateAddedDays == null) return true;
      if (!createdAt) return false;
      const ts = Date.parse(createdAt);
      if (Number.isNaN(ts)) return false;
      const cutoff = Date.now() - dateAddedDays * 24 * 60 * 60 * 1000;
      return ts >= cutoff;
    },
    [
      activeSelectedIds,
      effectiveKind,
      taxonomyIndex,
      dateAddedDays,
    ],
  );

  // colorForShape paints each shape through the ACTIVE lens: only chips
  // of `effectiveKind` are candidates, so switching the lens repaints the
  // map by a different dimension. When the lens has a selection we
  // restrict to it (so deselected chips stop painting / get filtered
  // out); with no selection the shape still color-codes by its
  // active-kind chip. A shape that carries no chip of the active kind
  // falls through to `defaultColor` (its legacy palette).
  //
  // `themeRanks` is optional and comes from the per-shape rank map
  // (e.g. polygon.theme_ranks, loc.theme_ranks) emitted by
  // selectProjectCont after migration 023. It only matters under the
  // Themes lens, where Primary > Secondary > Tertiary breaks ties; the
  // single-select kinds ignore it.
  const colorForShape = useCallback(
    (shapeTaxonomyIds, defaultColor, themeRanks = null) =>
      pickPrimaryChipColor(
        shapeTaxonomyIds,
        taxonomyIndex,
        defaultColor,
        activeSelectedIds.size > 0 ? activeSelectedIds : null,
        themeRanks,
        effectiveKind,
      ),
    [taxonomyIndex, activeSelectedIds, effectiveKind],
  );

  const value = useMemo(
    () => ({
      taxonomies,
      taxonomyIndex,
      activeThemeIds,
      activePropertyTypeIds,
      activeCommunityIds,
      // Exposed so the map can ask "which of THIS shape's eras matches the
      // current selection?" — needed to paint an out-of-era marker in the
      // hue of the era it points at (client request Aug 4 2026).
      activeSelectedIds,
      activeKind,
      effectiveKind,
      dateAddedDays,
      entryTypes,
      selectedContributorIds,
      regionsVisible,
      showMyPlaces,
      highlightMyPlaces,
      isAnyActive,
      isAnyTaxActive,
      isActiveLensFiltered,
      loading,
      toggleTheme,
      togglePropertyType,
      toggleCommunity,
      setActiveKind,
      setDateAddedDays,
      toggleEntryType,
      setEntryTypes,
      setSelectedContributorIds,
      setRegionsVisible,
      setShowMyPlaces,
      setHighlightMyPlaces,
      clearAll,
      refresh: reload,
      shapePassesFilter,
      colorForShape,
    }),
    [
      taxonomies,
      taxonomyIndex,
      activeThemeIds,
      activePropertyTypeIds,
      activeCommunityIds,
      // Exposed so the map can ask "which of THIS shape's eras matches the
      // current selection?" — needed to paint an out-of-era marker in the
      // hue of the era it points at (client request Aug 4 2026).
      activeSelectedIds,
      activeKind,
      effectiveKind,
      dateAddedDays,
      entryTypes,
      selectedContributorIds,
      regionsVisible,
      showMyPlaces,
      highlightMyPlaces,
      isAnyActive,
      isAnyTaxActive,
      isActiveLensFiltered,
      loading,
      toggleTheme,
      togglePropertyType,
      toggleCommunity,
      setActiveKind,
      setDateAddedDays,
      toggleEntryType,
      setEntryTypes,
      setSelectedContributorIds,
      setRegionsVisible,
      setShowMyPlaces,
      setHighlightMyPlaces,
      clearAll,
      reload,
      shapePassesFilter,
      colorForShape,
    ],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};
